import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from "@material-ui/pickers";
import { useMutation } from "@apollo/client";
import * as Sentry from "@sentry/browser";
import { Mutation, MutationCloseJobArgs } from "../../generated/nest-graphql";
import { CLOSE_JOB } from "../../graphql/mutations/closeJob";
import { useDispatch } from "../../contexts/snackbar-context";
import { showErrorAlert, showSuccessAlert } from "../../actions";

const ONE_HOUR_MS = 60 * 60 * 1000;

type CloseJobConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  jobId: string;
  // Earliest "Started Job" time. Used to decide whether the tech may backdate the
  // end time (only offered once the job has been open for more than an hour).
  jobStartedAt?: string | null;
  onSuccess?: () => void;
};

export const CloseJobConfirmDialog: React.FC<CloseJobConfirmDialogProps> = ({
  open,
  onClose,
  jobId,
  jobStartedAt,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [closeJob, { loading }] = useMutation<Pick<Mutation, "closeJob">, MutationCloseJobArgs>(CLOSE_JOB);

  const startedAtDate = jobStartedAt ? new Date(jobStartedAt) : null;
  const openedOverAnHour = !!startedAtDate && Date.now() - startedAtDate.getTime() > ONE_HOUR_MS;

  // When the job has been open > 1h, let the tech choose the actual end time
  // (defaults to now). Otherwise we always close at "now".
  const [endTime, setEndTime] = useState<Date | null>(new Date());

  const handleConfirm = async () => {
    try {
      const endedAt = openedOverAnHour ? endTime : undefined;
      if (openedOverAnHour) {
        if (!endTime) {
          showErrorAlert(dispatch, "Please choose an end time");
          return;
        }
        if (endTime.getTime() > Date.now()) {
          showErrorAlert(dispatch, "End time cannot be in the future");
          return;
        }
        if (startedAtDate && endTime.getTime() <= startedAtDate.getTime()) {
          showErrorAlert(dispatch, "End time must be after the job started");
          return;
        }
      }
      await closeJob({ variables: { jobId, endedAt } });
      showSuccessAlert(dispatch, "Job closed");
      onSuccess?.();
      onClose();
    } catch (e) {
      showErrorAlert(dispatch, "Could not close job");
      Sentry.captureException(e, { extra: { jobId } });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Close Job</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {openedOverAnHour ? "What time did this job end?" : "Are you sure you want to close this job?"}
        </DialogContentText>
        {openedOverAnHour && (
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDateTimePicker
              value={endTime}
              onChange={setEndTime}
              label="End time"
              format="MM/dd/yyyy hh:mm a"
              disableFuture
              ampm
              fullWidth
            />
          </MuiPickersUtilsProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={loading}>
          {loading ? "Closing..." : "Close Job"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
